import { useCallback, useEffect, useRef, useState } from "react";
import contractsV2Service from "../services/contractsV2Service";
import { Contract } from "../types/contract";

/** Live count-up while a download streams. There is no total, so no percentage. */
export interface ContractsDownloadProgress {
  rowCount: number;
  byteCount: number;
}

/** What the completed download produced, shown after the fact. */
export interface ContractsDownloadSummary {
  fileName: string;
  rowCount: number;
  byteCount: number;
  durationMs: number;
}

// UI updates are capped rather than driven per chunk: a 45 MB response arrives in thousands of
// chunks and a re-render for each would stall the tab.
const DOWNLOAD_PROGRESS_INTERVAL_MS = 100;

interface UseContractsParams {
  pageSize?: number;
}

export const useContracts = ({ pageSize: initialPageSize = 20 }: UseContractsParams = {}) => {
  // Data state
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Sort state
  const [sortColumn, setSortColumn] = useState<string>("conId");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchColumn, setSearchColumn] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialColumnMount = useRef(true);

  // Filter state
  const [optionsAllowed, setOptionsAllowed] = useState<boolean | undefined>(undefined);
  const [verifiedSinceMs, setVerifiedSinceMs] = useState<number | undefined>(undefined);

  // Selection state
  const [selectedConIds, setSelectedConIds] = useState<Set<number>>(new Set());
  
  // Bulk action state
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Download state
  const [downloadProgress, setDownloadProgress] = useState<ContractsDownloadProgress | null>(null);
  const [downloadSummary, setDownloadSummary] = useState<ContractsDownloadSummary | null>(null);
  const downloadAbortRef = useRef<AbortController | null>(null);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset page when filters change (skip initial render)
  useEffect(() => {
    if (isInitialColumnMount.current) {
      isInitialColumnMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchColumn, optionsAllowed, verifiedSinceMs]);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await contractsV2Service.findContracts({
        pageNumber: currentPage,
        pageSize,
        orderByColumn: sortColumn,
        orderByDirection: sortDirection,
        searchString: debouncedSearchQuery || undefined,
        searchColumn: searchColumn || undefined,
        optionsAllowed,
        verifiedSinceMs,
      });

      setContracts(response.items);
      setTotalItems(response.totalItems);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch contracts");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, sortColumn, sortDirection, debouncedSearchQuery, searchColumn, optionsAllowed, verifiedSinceMs]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedConIds(new Set(contracts.map((c) => c.conId)));
    } else {
      setSelectedConIds(new Set());
    }
  };

  const handleSelectOne = (conId: number, checked: boolean) => {
    setSelectedConIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(conId);
      } else {
        next.delete(conId);
      }
      return next;
    });
  };

  const deleteContracts = async () => {
    if (selectedConIds.size === 0) return;

    try {
      setDeleting(true);
      await contractsV2Service.deleteContracts(Array.from(selectedConIds));
      setSelectedConIds(new Set());
      await fetchContracts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete contracts");
    } finally {
      setDeleting(false);
    }
  };

  const verifyContracts = async (idsToVerify?: number[]) => {
    // Determine IDs to use: either passed arg or selected state
    const targetIds = idsToVerify || Array.from(selectedConIds);

    if (targetIds.length === 0) return;

    try {
      setBulkActionLoading(true);
      await contractsV2Service.setVerifications(targetIds);
      
      // If we verified specific IDs, we might want to clear selection or not?
      // If we verified selection, we definitely clear.
      // Let's clear selection if it matches.
      setSelectedConIds(new Set());
      
      await fetchContracts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set verifications");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const unverifyContracts = async () => {
    if (selectedConIds.size === 0) return;

    try {
      setBulkActionLoading(true);
      await contractsV2Service.clearVerifications(Array.from(selectedConIds));
      setSelectedConIds(new Set());
      await fetchContracts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear verifications");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const cancelDownload = () => {
    downloadAbortRef.current?.abort();
  };

  const downloadContracts = async () => {
    // A second download while one is in flight would abandon the first mid-stream.
    if (downloadAbortRef.current) return;

    const controller = new AbortController();
    downloadAbortRef.current = controller;

    const startedAt = Date.now();
    let lastProgressAt = 0;

    setDownloading(true);
    setDownloadSummary(null);
    setDownloadProgress({ rowCount: 0, byteCount: 0 });

    try {
      const result = await contractsV2Service.downloadContractsStream(
        {
          searchString: debouncedSearchQuery || undefined,
          searchColumn: searchColumn || undefined,
          optionsAllowed,
          verifiedSinceMs,
        },
        {
          signal: controller.signal,
          onProgress: (progress) => {
            const now = Date.now();
            if (now - lastProgressAt < DOWNLOAD_PROGRESS_INTERVAL_MS) return;
            lastProgressAt = now;
            setDownloadProgress(progress);
          },
        }
      );

      // Only saved once the stream has completed cleanly. A mid-stream failure throws, and the
      // partial data is discarded by the transport -- a truncated catalogue that looks whole
      // could be re-uploaded as the full set.
      const fileName = contractsV2Service.buildDownloadFileName();
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDownloadSummary({
        fileName,
        rowCount: result.rowCount,
        byteCount: result.byteCount,
        durationMs: Date.now() - startedAt,
      });
    } catch (err) {
      // A cancel is the admin's own doing, not a fault.
      if (err instanceof Error && err.name === "AbortError") return;
      setError(
        err instanceof Error
          ? `Download failed -- no file was saved. ${err.message}`
          : "Download failed -- no file was saved."
      );
    } finally {
      downloadAbortRef.current = null;
      setDownloading(false);
      setDownloadProgress(null);
    }
  };

  // An in-flight download must be aborted on unmount, or it keeps streaming from Postgres for a
  // page nobody is on.
  useEffect(() => () => downloadAbortRef.current?.abort(), []);

  return {
    contracts,
    loading,
    error,
    pagination: {
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize,
      totalItems,
      totalPages
    },
    sorting: {
      sortColumn,
      sortDirection,
      handleSort
    },
    search: {
      searchQuery,
      setSearchQuery,
      searchColumn,
      setSearchColumn
    },
    filters: {
      optionsAllowed,
      setOptionsAllowed,
      verifiedSinceMs,
      setVerifiedSinceMs
    },
    selection: {
      selectedConIds,
      handleSelectAll,
      handleSelectOne,
      setSelectedConIds
    },
    actions: {
      fetchContracts,
      deleteContracts,
      verifyContracts,
      unverifyContracts,
      downloadContracts,
      cancelDownload,
      bulkActionLoading,
      downloading,
      downloadProgress,
      downloadSummary,
      deleting
    }
  };
};
